/**
 * Geocoding Queue Service
 *
 * Manages a queue of geocoding requests with rate limiting to respect
 * Nominatim's 1 request per second limit. Processes requests sequentially
 * with exponential backoff for rate-limited requests.
 */

import { geocodeAddressWithFallback, GeocodeResult, GeocodeResponse } from './geocoding';

interface GeocodingRequest {
  streetAddress: string;
  addressLine2: string;
  city: string;
  province: string;
  postalCode: string;
  resolve: (result: GeocodeResult | null) => void;
  reject: (error: Error) => void;
}

class GeocodingQueue {
  private queue: GeocodingRequest[] = [];
  private processing = false;
  private readonly MIN_REQUEST_INTERVAL = 1100; // 1100ms > 1 second to respect rate limits
  private readonly MAX_RETRIES = 5; // Increased from 3 to handle rate limiting better
  private readonly BASE_RETRY_DELAY = 2000; // Start at 2 seconds for exponential backoff
  private readonly MAX_RETRY_DELAY = 30000; // Cap at 30 seconds

  /**
   * Add a geocoding request to the queue
   * @param streetAddress - Street address
   * @param addressLine2 - Optional address line 2
   * @param city - City
   * @param province - Province/State
   * @param postalCode - Postal code
   * @returns Promise that resolves with geocode result or null if failed
   */
  public enqueue(
    streetAddress: string,
    addressLine2: string,
    city: string,
    province: string,
    postalCode: string
  ): Promise<GeocodeResult | null> {
    return new Promise((resolve, reject) => {
      this.queue.push({ streetAddress, addressLine2, city, province, postalCode, resolve, reject });
      const formattedAddress = `${streetAddress}, ${city}, ${province}`;
      console.log(`[GeocodingQueue] Enqueued address: "${formattedAddress}" (Queue size: ${this.queue.length})`);

      // Start processing if not already running
      if (!this.processing) {
        this.processQueue();
      }
    });
  }

  /**
   * Process the queue sequentially with intelligent retry logic and exponential backoff
   */
  private async processQueue(): Promise<void> {
    if (this.processing) {
      return;
    }

    this.processing = true;

    while (this.queue.length > 0) {
      const request = this.queue.shift();
      if (!request) {
        continue;
      }

      try {
        const formattedAddress = `${request.streetAddress}, ${request.city}, ${request.province}`;

        // Retry logic with exponential backoff
        let response: GeocodeResponse | null = null;
        let attempts = 0;

        while (attempts < this.MAX_RETRIES) {
          attempts++;

          if (attempts > 1) {
            console.log(`[GeocodingQueue] Retry attempt ${attempts}/${this.MAX_RETRIES} for: "${formattedAddress}"`);
          } else {
            console.log(`[GeocodingQueue] Processing: "${formattedAddress}"`);
          }

          // Call geocodeAddressWithFallback which tries up to 5 address formats
          response = await geocodeAddressWithFallback(
            request.streetAddress,
            request.addressLine2,
            request.city,
            request.province,
            request.postalCode
          );

          // If successful, break out of retry loop
          if (response.success && response.result) {
            console.log(
              `[GeocodingQueue] ✓ Success: "${formattedAddress}" → [${response.result.lat}, ${response.result.lng}]` +
                (attempts > 1 ? ` (succeeded on attempt ${attempts})` : '')
            );
            request.resolve(response.result);
            break;
          }

          // Handle different error types
          const error = response.error!;

          // For NOT_FOUND errors, don't retry (address doesn't exist)
          if (error.code === 'NOT_FOUND') {
            console.warn(`[GeocodingQueue] ❌ Address not found: "${formattedAddress}" - no retry`);
            request.resolve(null);
            break;
          }

          // For RATE_LIMIT errors, use exponential backoff
          if (error.code === 'RATE_LIMIT') {
            if (attempts < this.MAX_RETRIES) {
              // Calculate exponential backoff delay
              const backoffDelay = Math.min(this.BASE_RETRY_DELAY * Math.pow(2, attempts - 1), this.MAX_RETRY_DELAY);

              // Use API's retry-after hint if available
              const delayMs = error.retryAfter ? error.retryAfter * 1000 : backoffDelay;

              console.warn(
                `[GeocodingQueue] ⚠️ Rate limited: "${formattedAddress}" - ` +
                  `waiting ${(delayMs / 1000).toFixed(1)}s before retry ${attempts + 1}/${this.MAX_RETRIES}`
              );
              await this.delay(delayMs);
            } else {
              console.error(
                `[GeocodingQueue] ❌ Rate limit: "${formattedAddress}" - exhausted all ${this.MAX_RETRIES} retries`
              );
              request.resolve(null);
              break;
            }
          } else {
            // For other errors (NETWORK_ERROR, UNKNOWN), use standard retry interval
            if (attempts < this.MAX_RETRIES) {
              console.warn(`[GeocodingQueue] ⚠️ Error (${error.code}): ${error.message} - retrying...`);
              await this.delay(this.MIN_REQUEST_INTERVAL);
            } else {
              console.error(
                `[GeocodingQueue] ❌ Failed: "${formattedAddress}" - exhausted all ${this.MAX_RETRIES} retries`
              );
              request.resolve(null);
              break;
            }
          }
        }
      } catch (error) {
        console.error(`[GeocodingQueue] ❌ Exception processing request:`, error);
        request.reject(error instanceof Error ? error : new Error('Unknown geocoding error'));
      }
    }

    this.processing = false;
    console.log('[GeocodingQueue] Queue processing complete');
  }

  /**
   * Utility function to delay execution
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get the current queue size
   */
  public getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Check if the queue is currently processing
   */
  public isProcessing(): boolean {
    return this.processing;
  }

  /**
   * Clear all pending requests from the queue
   */
  public clear(): void {
    const clearedCount = this.queue.length;
    this.queue.forEach(request => {
      request.reject(new Error('Queue cleared'));
    });
    this.queue = [];
    console.log(`[GeocodingQueue] Cleared ${clearedCount} pending requests`);
  }
}

// Export a singleton instance
export const geocodingQueue = new GeocodingQueue();

// Export the class for testing purposes
export { GeocodingQueue };
